export function createInitialBoard() {
  const board = Array.from({ length: 8 }, () => Array(8).fill(null));
  for (let row = 0; row < 8; row += 1) {
    for (let col = 0; col < 8; col += 1) {
      if ((row + col) % 2 !== 1) continue;
      if (row <= 2) board[row][col] = 'b';
      if (row >= 5) board[row][col] = 'r';
    }
  }
  return board;
}

export function parseSquare(square) {
  const text = String(square || '').trim().toLowerCase();
  const match = /^([a-h])([1-8])$/.exec(text);
  if (!match) return null;
  const col = match[1].charCodeAt(0) - 'a'.charCodeAt(0);
  const row = 7 - (Number(match[2]) - 1);
  return { row, col };
}

export function formatSquare(row, col) {
  const file = String.fromCharCode('a'.charCodeAt(0) + col);
  const rank = String(8 - row);
  return `${file}${rank}`;
}

function isInside(row, col) {
  return row >= 0 && row < 8 && col >= 0 && col < 8;
}

function cloneBoard(board) {
  return board.map((row) => row.slice());
}

function pieceColor(piece) {
  if (!piece) return null;
  const p = String(piece).toLowerCase();
  if (p === 'r') return 'r';
  if (p === 'b') return 'b';
  return null;
}

function isKing(piece) {
  return piece === 'R' || piece === 'B';
}

function shouldPromote(piece, row) {
  if (piece === 'r' && row === 0) return true;
  if (piece === 'b' && row === 7) return true;
  return false;
}

function promote(piece) {
  if (piece === 'r') return 'R';
  if (piece === 'b') return 'B';
  return piece;
}

function simpleDirs(color, king) {
  if (king) return [[-1, -1], [-1, 1], [1, -1], [1, 1]];
  if (color === 'r') return [[-1, -1], [-1, 1]];
  return [[1, -1], [1, 1]];
}

function jumpDirs(color, king) {
  if (king) return [[-2, -2], [-2, 2], [2, -2], [2, 2]];
  if (color === 'r') return [[-2, -2], [-2, 2]];
  return [[2, -2], [2, 2]];
}

function captureMovesForPiece(board, row, col) {
  const piece = board[row][col];
  if (!piece) return [];
  const color = pieceColor(piece);
  const dirs = jumpDirs(color, isKing(piece));
  const out = [];

  for (const [dr, dc] of dirs) {
    const overRow = row + dr / 2;
    const overCol = col + dc / 2;
    const nr = row + dr;
    const nc = col + dc;
    if (!isInside(overRow, overCol) || !isInside(nr, nc)) continue;
    const overPiece = board[overRow][overCol];
    if (!overPiece || pieceColor(overPiece) === color) continue;
    if (board[nr][nc] !== null) continue;

    out.push({
      from: formatSquare(row, col),
      to: formatSquare(nr, nc),
      captures: [formatSquare(overRow, overCol)],
    });
  }

  return out;
}

function simpleMovesForPiece(board, row, col) {
  const piece = board[row][col];
  if (!piece) return [];
  const color = pieceColor(piece);
  const dirs = simpleDirs(color, isKing(piece));
  const out = [];

  for (const [dr, dc] of dirs) {
    const nr = row + dr;
    const nc = col + dc;
    if (!isInside(nr, nc)) continue;
    if (board[nr][nc] !== null) continue;
    out.push({
      from: formatSquare(row, col),
      to: formatSquare(nr, nc),
      captures: [],
    });
  }

  return out;
}

export function getLegalMoves(board, turnColor, forcedSquare = null) {
  const captures = [];
  const simple = [];

  for (let row = 0; row < 8; row += 1) {
    for (let col = 0; col < 8; col += 1) {
      const piece = board[row][col];
      if (!piece || pieceColor(piece) !== turnColor) continue;
      const sq = formatSquare(row, col);
      if (forcedSquare && sq !== forcedSquare) continue;

      const cap = captureMovesForPiece(board, row, col);
      if (cap.length) captures.push(...cap);
      else if (!forcedSquare) simple.push(...simpleMovesForPiece(board, row, col));
    }
  }

  if (captures.length) return captures;
  return forcedSquare ? [] : simple;
}

export function applyMove(boardInput, turnColor, from, to, forcedSquare = null) {
  const board = cloneBoard(boardInput);
  const fromPos = parseSquare(from);
  const toPos = parseSquare(to);
  if (!fromPos || !toPos) return { ok: false, error: 'Invalid square' };

  const piece = board[fromPos.row][fromPos.col];
  if (!piece || pieceColor(piece) !== turnColor) return { ok: false, error: 'Invalid piece' };
  if (forcedSquare && formatSquare(fromPos.row, fromPos.col) !== forcedSquare) {
    return { ok: false, error: `You must continue from ${forcedSquare}` };
  }

  const legal = getLegalMoves(board, turnColor, forcedSquare);
  const selected = legal.find((m) => m.from === from && m.to === to);
  if (!selected) return { ok: false, error: 'Illegal move' };

  board[fromPos.row][fromPos.col] = null;
  let movedPiece = piece;

  const didCapture = selected.captures.length > 0;
  if (didCapture) {
    selected.captures.forEach((sq) => {
      const pos = parseSquare(sq);
      if (pos) board[pos.row][pos.col] = null;
    });
  }

  if (shouldPromote(movedPiece, toPos.row)) movedPiece = promote(movedPiece);
  board[toPos.row][toPos.col] = movedPiece;

  let nextTurn = turnColor === 'r' ? 'b' : 'r';
  let mustContinueFrom = null;
  const promotedNow = movedPiece !== piece;

  if (didCapture && !promotedNow) {
    const followUp = captureMovesForPiece(board, toPos.row, toPos.col);
    if (followUp.length) {
      nextTurn = turnColor;
      mustContinueFrom = formatSquare(toPos.row, toPos.col);
    }
  }

  return {
    ok: true,
    board,
    move: {
      from,
      to,
      captures: selected.captures,
      promoted: promotedNow,
      piece,
      pieceAfter: movedPiece,
    },
    didCapture,
    promotedNow,
    nextTurn,
    mustContinueFrom,
  };
}

export function getSimpleBotMove(board, color, forcedSquare = null) {
  const legal = getLegalMoves(board, color, forcedSquare);
  if (!legal.length) return null;
  const captures = legal.filter((m) => (m.captures || []).length > 0);
  const pool = captures.length ? captures : legal;
  return pool[Math.floor(Math.random() * pool.length)];
}

// ─── Full Bot Engine (level 1=easy, 2=medium, 3=hard) ──────────────────────

function countAllPiecesBot(board) {
  let r = 0; let R = 0; let b = 0; let B = 0;
  for (let row = 0; row < 8; row += 1) {
    for (let col = 0; col < 8; col += 1) {
      const p = board[row][col];
      if (p === 'r') r += 1;
      else if (p === 'R') R += 1;
      else if (p === 'b') b += 1;
      else if (p === 'B') B += 1;
    }
  }
  return { r, R, b, B };
}

function evaluateBoardBot(board, botColor) {
  const pieces = countAllPiecesBot(board);
  const oppColor = botColor === 'b' ? 'r' : 'b';
  const botMen   = botColor === 'b' ? pieces.b : pieces.r;
  const botKings = botColor === 'b' ? pieces.B : pieces.R;
  const oppMen   = oppColor === 'b' ? pieces.b : pieces.r;
  const oppKings = oppColor === 'b' ? pieces.B : pieces.R;
  let score = (botMen * 1 + botKings * 3) - (oppMen * 1 + oppKings * 3);
  for (let row = 0; row < 8; row += 1) {
    for (let col = 0; col < 8; col += 1) {
      const p = board[row][col];
      if (!p) continue;
      const pc = String(p).toLowerCase();
      const sign = pc === botColor ? 1 : -1;
      const isK = p === 'R' || p === 'B';
      const distFromCenter = Math.abs(row - 3.5) + Math.abs(col - 3.5);
      score += sign * (5 - distFromCenter) * 0.05;
      if (!isK) {
        const advancement = pc === 'b' ? row : (7 - row);
        score += sign * advancement * 0.08;
      }
    }
  }
  return score;
}

function scoreMoveBot(board, color, move, depth) {
  const result = applyMove(board, color, move.from, move.to);
  if (!result.ok) return -999;
  let finalBoard = result.board;
  let cont = result.mustContinueFrom;
  while (cont) {
    const followups = getLegalMoves(finalBoard, color, cont);
    if (!followups.length) break;
    const pick = followups.reduce((best, m) =>
      (m.captures || []).length > (best.captures || []).length ? m : best,
      followups[0]
    );
    const chained = applyMove(finalBoard, color, pick.from, pick.to, cont);
    if (!chained.ok) break;
    finalBoard = chained.board;
    cont = chained.mustContinueFrom;
  }
  if (depth <= 0) return evaluateBoardBot(finalBoard, color);
  const oppColor = color === 'r' ? 'b' : 'r';
  const oppMoves = getLegalMoves(finalBoard, oppColor);
  if (!oppMoves.length) return 100;
  let worstForUs = Infinity;
  for (const oppMove of oppMoves) {
    const oppScore = scoreMoveBot(finalBoard, oppColor, oppMove, depth - 1);
    const ourView = -oppScore;
    if (ourView < worstForUs) worstForUs = ourView;
  }
  return worstForUs;
}

/**
 * getBotMove – tiered checkers bot
 * level 1 = easy (random, takes captures), 2 = medium (greedy), 3 = hard (minimax depth-3)
 */
export function getBotMove(board, color, forcedSquare = null, level = 2) {
  const legal = getLegalMoves(board, color, forcedSquare);
  if (!legal.length) return null;
  if (level <= 1) {
    const captures = legal.filter((m) => (m.captures || []).length > 0);
    const pool = captures.length ? captures : legal;
    return pool[Math.floor(Math.random() * pool.length)];
  }
  if (level === 2) {
    const captures = legal.filter((m) => (m.captures || []).length > 0);
    const pool = captures.length ? captures : legal;
    const scored = pool.map((m) => ({ move: m, score: scoreMoveBot(board, color, m, 0) }));
    scored.sort((a, b) => b.score - a.score);
    const top = scored.slice(0, Math.min(3, scored.length));
    return top[Math.floor(Math.random() * top.length)].move;
  }
  // level 3 – minimax 3-ply
  const scored = legal.map((m) => ({ move: m, score: scoreMoveBot(board, color, m, 3) }));
  scored.sort((a, b) => b.score - a.score);
  const bestScore = scored[0].score;
  const topMoves = scored.filter((e) => e.score >= bestScore - 0.01);
  return topMoves[Math.floor(Math.random() * topMoves.length)].move;
}
